import React from 'react';

import classNames from 'classnames';
import TrashIcon from 'icons/ic-trash.svg';
import AccordionArrowIcon from 'images/icons/accordian_arrow_right.svg';
import voca from 'voca';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import Tooltip from 'components/application/Tooltip';

import ReviewStatusIndicator from 'components/contentRegister/components/ReviewStatusIndicator';
import SharedContentBadge from 'components/contentRegister/components/SharedContentBadge';
import DragHandle from 'components/contentRegister/components/sortableTable/components/DragHandle';
import { editCategoryPath } from 'components/contentRegister/helpers/paths';

export default function buildCategoryColumns({ riskRegisterPolicy }) {
  return [
    {
      id: 'dragHandle',
      header: '',
      cell: ({ row, table }) => (
        <div
          className='tw-ml-3 tw-flex tw-items-center tw-justify-center'
          onClick={(event) => event.stopPropagation()}
        >
          <DragHandle
            label={table.options.meta.translations.reorderName(
              row.original.attributes.name,
            )}
          />
        </div>
      ),
      meta: { classNames: 'tw-w-5' },
    },
    {
      id: 'toggle',
      header: '',
      cell: ({ row }) => (
        <div className='tw-flex tw-items-center tw-justify-center'>
          <AccordionArrowIcon
            aria-hidden='true'
            className={classNames(
              'tw-transition-transform [&_path]:tw-fill-grey-700',
              row.getIsExpanded() && 'tw-rotate-90',
            )}
            height={32}
            width={32}
          />
        </div>
      ),
      meta: { classNames: 'tw-w-8' },
    },
    {
      id: 'name',
      header: ({ table }) => table.options.meta.translations.category,
      cell: ({ row, table }) => (
        <div
          className='tw-flex tw-items-center tw-gap-2'
          data-element-name='category-name'
        >
          <span className='tw-truncate tw-font-medium'>
            {row.original.attributes.name}
          </span>
          {!row.original.attributes.isPublished && (
            <span className='tw-shrink-0 tw-rounded-lg tw-bg-grey-100 tw-px-2 tw-py-1 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-600'>
              {table.options.meta.translations.draft}
            </span>
          )}
        </div>
      ),
      meta: { classNames: 'tw-p-2 tw-text-left' },
    },
    {
      id: 'shared',
      header: '',
      cell: ({ row, table }) => {
        if (!row.original.meta.isSharedAcrossDivisions) return null;

        const {
          attributes: { name },
          meta: { areAllSharedDivisionsVisible },
          relationships: {
            contentEditableDivisions: { data: contentEditableDivisionsData },
          },
        } = row.original;

        return (
          <div className='tw-flex tw-items-center tw-justify-center'>
            <SharedContentBadge
              areAllVisible={areAllSharedDivisionsVisible}
              recordName={name}
              sharedNames={contentEditableDivisionsData.map(
                ({ id: companyId }) =>
                  table.options.meta.included.company[companyId].attributes
                    .name,
              )}
              variant='divisions'
            />
          </div>
        );
      },
      meta: { classNames: 'tw-p-2 tw-w-43' },
    },
    riskRegisterPolicy.canShow && {
      id: 'status',
      header: ({ table }) => table.options.meta.translations.status,
      cell: ({ row }) => (
        <div className='tw-flex tw-items-center tw-justify-center'>
          <ReviewStatusIndicator
            status={voca.camelCase(row.original.meta.reviewStatus)}
            variant='category'
          />
        </div>
      ),
      meta: { classNames: 'tw-p-2 tw-w-15 tw-text-center' },
    },
    {
      id: 'sequences',
      header: ({ table }) => table.options.meta.translations.sequences,
      cell: ({ row }) => (
        <span data-element-name='category-sequences'>
          {row.original.meta.masterMethodSequencesCount}
        </span>
      ),
      meta: { classNames: 'tw-p-2 tw-w-36 tw-text-left' },
    },
    {
      id: 'methodStatements',
      header: ({ table }) => table.options.meta.translations.methodStatements,
      cell: ({ row }) => (
        <span data-element-name='category-method-statements'>
          {row.original.meta.masterMethodTextsCount}
        </span>
      ),
      meta: { classNames: 'tw-p-2 tw-w-36 tw-text-left' },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row, table }) => {
        const { onDeleteClick, translations } = table.options.meta;

        const {
          attributes: { name },
          id: categoryId,
          meta: { isManageable },
        } = row.original;

        return (
          <div
            className='tw-flex tw-items-center tw-justify-center tw-gap-2'
            onClick={(event) => event.stopPropagation()}
          >
            <ConditionalWrapper
              condition={!isManageable}
              wrapper={(children) => (
                <Tooltip
                  className='tooltip-dark--max-w-xs'
                  placement='top'
                  tooltip={translations.noAccessToAllDivisionsUsingCategory}
                  trigger='hover'
                >
                  {children}
                </Tooltip>
              )}
            >
              <OutlinedButton
                color='grey'
                disabled={!isManageable}
                href={isManageable ? editCategoryPath(categoryId) : '#0'}
                size='sm'
              >
                {translations.editCategory}
              </OutlinedButton>
            </ConditionalWrapper>
            <ConditionalWrapper
              condition={!isManageable}
              wrapper={(children) => (
                <Tooltip
                  className='tooltip-dark--max-w-xs'
                  placement='top'
                  tooltip={translations.noAccessToAllDivisionsUsingCategory}
                  trigger='hover'
                >
                  {children}
                </Tooltip>
              )}
            >
              <button
                aria-label={translations.deleteCategory(name)}
                className={classNames(
                  'tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-p-1',
                  isManageable ?
                    'tw-bg-transparent hover:tw-bg-red-025 active:tw-bg-red-050 [&_svg_path]:tw-fill-grey-500 [&_svg_path]:hover:tw-fill-red-600'
                  : 'tw-bg-grey-050 [&_svg_path]:tw-fill-grey-300',
                )}
                data-element-name='category-delete'
                disabled={!isManageable}
                onClick={() => onDeleteClick(categoryId)}
                type='button'
              >
                <TrashIcon height={24} width={24} />
              </button>
            </ConditionalWrapper>
          </div>
        );
      },
      meta: { classNames: 'tw-w-46' },
    },
  ].filter(Boolean);
}
